<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import Tooltip from '@/components/shadcn/Tooltip.vue'
import TooltipContent from '@/components/shadcn/TooltipContent.vue'
import TooltipProvider from '@/components/shadcn/TooltipProvider.vue'
import TooltipTrigger from '@/components/shadcn/TooltipTrigger.vue'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'warning'   // 경고 스타일 (주황)
  | 'danger'    // 위험 스타일 (빨강)

type Size = 'xs' | 'sm' | 'md' | 'lg'

type Trigger = 'hover' | 'focus' | 'click' | 'manual'

const props = withDefaults(
  defineProps<{
    content?: string
    side?: 'top' | 'right' | 'bottom' | 'left'
    align?: 'start' | 'center' | 'end'
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 툴팁 크기 */
    size?: Size
    /** 툴팁 비활성화 */
    disabled?: boolean
    /** 표시 지연 시간 (ms) */
    delay?: number
    /** 툴팁 최대 너비 */
    maxWidth?: string | number
    /** 툴팁 트리거 타입 */
    trigger?: Trigger
  }>(),
  {
    side: 'top',
    align: 'center',
    styletype: 'default',
    size: 'sm',
    disabled: false,
    delay: 200,
    maxWidth: '200px',
    trigger: 'hover',
  },
)

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  primary: { 
    class: 'bg-blue-500 text-white border-blue-600',
  },
  success: { 
    class: 'bg-green-500 text-white border-green-600',
  },
  warning: { 
    class: 'bg-amber-500 text-white border-amber-600',
  },
  danger: { 
    class: 'bg-red-500 text-white border-red-600',
  },
}

/**
 * size -> class 매핑
 */
const SIZE_PRESETS: Record<Size, { class: string }> = {
  xs: { 
    class: 'text-[10px] px-1.5 py-0.5 max-w-40',
  },
  sm: { 
    class: 'text-xs px-2 py-1 max-w-48',
  },
  md: { 
    class: 'text-xs px-2.5 py-1 max-w-56',
  },
  lg: { 
    class: 'text-sm px-3 py-1.5 max-w-64',
  },
}

// click과 manual 트리거를 위한 상태 관리
const isOpen = ref(false)

// 각 트리거 타입별 핸들러
const handleClick = (event: MouseEvent) => {
  console.log('Click handler called, trigger:', props.trigger)
  if (props.trigger === 'click') {
    console.log('Toggling tooltip, current state:', isOpen.value)
    isOpen.value = !isOpen.value
    console.log('New state:', isOpen.value)
  } else if (props.trigger === 'focus') {
    // focus 트리거에서는 클릭 이벤트 무시
    event.preventDefault()
    event.stopPropagation()
  }
}

// delay를 위한 타이머 관리
let hoverTimer: number | null = null

const handleMouseEnter = () => {
  if (props.trigger === 'hover') {
    // 기존 타이머가 있으면 취소
    if (hoverTimer) {
      clearTimeout(hoverTimer)
    }
    // delay 적용
    hoverTimer = setTimeout(() => {
      isOpen.value = true
    }, props.delay)
  }
}

const handleMouseLeave = () => {
  if (props.trigger === 'hover') {
    // 타이머 취소
    if (hoverTimer) {
      clearTimeout(hoverTimer)
      hoverTimer = null
    }
    isOpen.value = false
  }
}

const handleFocus = () => {
  if (props.trigger === 'focus') {
    isOpen.value = true
  }
}

const handleBlur = () => {
  if (props.trigger === 'focus') {
    isOpen.value = false
  }
}

// 트리거 타입에 따른 Tooltip 설정 계산
const tooltipConfig = computed(() => {
  const config: any = {
    disabled: props.disabled,
  }
  
  // 모든 트리거 타입에 대해 수동 제어
  config.open = isOpen.value
  
  return config
})

// 외부 클릭 감지를 위한 핸들러
const handleOutsideClick = (event: Event) => {
  if (props.trigger === 'hover' && isOpen.value) {
    const target = event.target as HTMLElement
    const tooltipElement = document.querySelector('[data-tooltip-trigger]')
    if (tooltipElement && !tooltipElement.contains(target)) {
      isOpen.value = false
    }
  }
}

// manual 트리거를 위한 메서드들
const showTooltip = () => {
  if (props.trigger === 'manual') {
    isOpen.value = true
  }
}

const hideTooltip = () => {
  if (props.trigger === 'manual') {
    isOpen.value = false
  }
}

// 외부에서 사용할 수 있도록 expose
defineExpose({
  showTooltip,
  hideTooltip,
})

// 외부 클릭 감지를 위한 이벤트 리스너 등록/해제
onMounted(() => {
  if (props.trigger === 'hover') {
    document.addEventListener('click', handleOutsideClick)
  }
})

onUnmounted(() => {
  if (props.trigger === 'hover') {
    document.removeEventListener('click', handleOutsideClick)
  }
  
  // 타이머 정리
  if (hoverTimer) {
    clearTimeout(hoverTimer)
  }
})

/** 최종 바인딩: 직접 넘긴 class가 있으면 styletype과 size 기본값과 병합 */
const mapped = computed(() => {
  const stylePreset = STYLE_PRESETS[props.styletype!]
  const sizePreset = SIZE_PRESETS[props.size!]
  const finalClass = [stylePreset.class, sizePreset.class, props.class].filter(Boolean).join(' ')
  
  // maxWidth 스타일 처리
  const maxWidthStyle = typeof props.maxWidth === 'number' 
    ? `${props.maxWidth}px` 
    : props.maxWidth
  
  return {
    side: props.side,
    align: props.align,
    class: finalClass,
    style: {
      maxWidth: maxWidthStyle,
    },
  }
})
</script>

<template>
  <TooltipProvider :delay-duration="props.delay">
    <Tooltip v-bind="tooltipConfig">
      <TooltipTrigger 
        as-child
        :data-tooltip-trigger="true"
        @click="handleClick"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        <slot name="trigger" />
      </TooltipTrigger>
      <TooltipContent v-bind="mapped">
        {{ props.content }}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
